import {
  DRONECAST_PROTOCOL,
  IDCMessage,
  DCCastroomId,
  DCPayloadCastingStart,
  DCPayloadIceCandidate,
  DCPayloadCastroomJoin,
  DCPayloadStreamSubscribe,
  DCPayloadCastroomClose,
  DCLegacyMessageType,
} from '@dronecast/shared'
import type { CastroomId, MediaServerType, VideoSourceType } from '@dronecast/shared'

export class DronecastClient {
  private ws: WebSocket | null = null;
  private messageHandlers: Map<string, (message: IDCMessage) => void> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private pendingMessages: IDCMessage[] = [];

  private type: 'caster' | 'viewer' = 'caster';

  private castroomId: CastroomId | undefined = undefined;
  private rtspUrl: string | undefined = undefined;

  private connectionUrl: string = '';

  public constructor(type: 'caster' | 'viewer') {
    this.type = type;
  }

  public connect(url: string) {
    if (this.isConnecting) return;
    this.isConnecting = true;

    try {
      this.connectionUrl = url;

      this.ws = new WebSocket(this.connectionUrl);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.sendPendingMessages();
      };

      this.ws.onmessage = async (event) => {
        try {
          const message: IDCMessage = JSON.parse(event.data);
          const handler = this.messageHandlers.get(message.type);
          if (handler) {
            await handler(message);
          }
        } catch (error) {
          console.error('메시지 처리 오류:', error);
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.reconnect();
      };

      this.ws.onerror = () => {
        this.isConnecting = false;
      };

    } catch (error) {
      console.error('WebSocket 연결 실패:', error);
      this.isConnecting = false;
      this.reconnect();
    }
  }

  private reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;

    this.reconnectAttempts++;
    setTimeout(() => {
      this.connect(this.connectionUrl);
    }, this.reconnectDelay);
  }

  private sendPendingMessages() {
    while (this.pendingMessages.length > 0) {
      const message = this.pendingMessages.shift();
      if (message) {
        this.send(message);
      }
    }
  }

  private toCastroomId(id: CastroomId): DCCastroomId {
    return { gcs: id.gcs, drone: id.drone, camera: id.camera }
  }

  public createCastroom(castroomId: CastroomId, rtspUrl?: string, mediaServerType?: MediaServerType, sourceType?: VideoSourceType) {
    this.castroomId = castroomId;
    this.rtspUrl = rtspUrl;

    this.send({
      protocol: DRONECAST_PROTOCOL,
      type: 'create_castroom',
      payload: {
        castroomId: this.toCastroomId(castroomId),
        rtspUrl: this.rtspUrl,
        ...(mediaServerType && { mediaServerType }),
        ...(sourceType && { sourceType }),
      }
    });
  }

  public exchangeIceCandidate(candidate: RTCIceCandidate) {
    if (!candidate || !this.castroomId) return;

    this.send({
      protocol: DRONECAST_PROTOCOL,
      type: 'exchange_icecandidate',
      payload: {
        castroomId: this.toCastroomId(this.castroomId),
        type: this.type,
        candidate,
      } as DCPayloadIceCandidate
    });
  }

  public castStream(sdpOffer: string) {
    if (!this.castroomId) return;

    this.send({
      protocol: DRONECAST_PROTOCOL,
      type: 'cast_stream',
      payload: {
        castroomId: this.toCastroomId(this.castroomId),
        sdpOffer,
      } as DCPayloadCastingStart
    });
  }

  public closeCastroom(castroomId?: CastroomId, reason?: 'caster_stopped' | 'admin_closed' | 'error') {
    const targetId = castroomId || this.castroomId!;

    this.send({
      protocol: DRONECAST_PROTOCOL,
      type: 'close_castroom',
      payload: {
        castroomId: this.toCastroomId(targetId),
        reason,
      } as DCPayloadCastroomClose
    });
  }

  public joinCastroom(castroomId: CastroomId, retryCount?: number) {
    this.castroomId = castroomId;

    this.send({
      protocol: DRONECAST_PROTOCOL,
      type: 'join_castroom',
      payload: {
        castroomId: this.toCastroomId(castroomId),
        retryCount: retryCount || 0,
      } as DCPayloadCastroomJoin
    });
  }

  public subscribeStream(sdpOffer: string) {
    if (!this.castroomId) return;

    this.send({
      protocol: DRONECAST_PROTOCOL,
      type: 'subscribe_stream',
      payload: {
        castroomId: this.toCastroomId(this.castroomId),
        sdpOffer,
      } as DCPayloadStreamSubscribe
    });
  }

  public send(message: IDCMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.pendingMessages.push(message);
      this.reconnect();
      return;
    }

    try {
      this.ws.send(JSON.stringify(message));
    } catch (error) {
      console.error('메시지 전송 실패:', error);
      this.pendingMessages.push(message);
    }
  }

  public setMessageHandler(type: DCLegacyMessageType | string, handler: (message: IDCMessage) => void) {
    this.messageHandlers.set(type, handler);
  }

  public removeHandler(type: string) {
    this.messageHandlers.delete(type);
  }

  public close() {
    if (this.ws) {
      this.ws.close();
    }
  }
}
